import React, { useEffect, useRef, useState } from "react";
import { Upload, Check, IndianRupee, X, Loader2 } from "lucide-react";
import vendor_profile_icon from "../../../assets/profile_icon.svg";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { resolveTransactionFeedback } from "utils/transactionFeedback";

const AdvancePayment = ({ vendor, onClose }) => {
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [remarks, setRemarks] = useState("");
  const [isToday, setIsToday] = useState(true);
  const [receiptFile, setReceiptFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const { addAlert } = useAlert();

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setIsToday(paymentDate === today);
  }, [paymentDate]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validTypes.includes(file.type)) {
      addAlert("Please upload an image, PDF, or document file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      addAlert("File size should be less than 5MB", "error");
      return;
    }

    setReceiptFile(file);
  };

  const handleRemoveFile = () => setReceiptFile(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (submitInFlightRef.current || !isFormValid) return;

    try {
      submitInFlightRef.current = true;
      setIsSubmitting(true);
      const accessToken = localStorage.getItem("access");
      const formData = new FormData();

      formData.append("vendor", vendor.id);
      formData.append("amount", paidAmount);
      formData.append("payment_date", paymentDate);
      formData.append("payment_method", paymentMethod);
      formData.append("remarks", remarks);

      if (receiptFile) formData.append("receipt", receiptFile);

      const response = await fetch(
        `${BASE_URL}/inventory/create-vendor-advance-payment/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        },
      );

      const data = await response.json();
      const feedback = resolveTransactionFeedback(data, {
        successMessage: "Advance payment recorded successfully",
        errorMessage: "Failed to record advance payment",
      });

      if (feedback.success) {
        addAlert(feedback.message, feedback.alertType);
        onClose();
      } else {
        addAlert(feedback.message, "error");
      }
    } catch (error) {
      addAlert(error.message || "Failed to record advance payment", "error");
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    Boolean(paymentDate) &&
    Boolean(paymentMethod) &&
    paidAmount !== "" &&
    Number(paidAmount) > 0;

  return (
    <div className="w-full h-full min-h-0 text-black flex flex-col">
      <div className="flex items-center justify-between py-4 px-3 border-b border-gray-200">
        <div className="relative w-full flex items-center justify-center">
          <p className="text-[#232323] text-[20px]">Advance Payment</p>
          <button
            onClick={onClose}
            className="absolute right-4 p-1.5 hover:bg-gray-100 rounded-full transition"
            type="button"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <img src={vendor_profile_icon} alt="Vendor" className="w-10 h-10" />
            <div className="text-[#232323] text-[18px]">
              {vendor?.name}
              <span className="text-[12px] bg-gray-200 px-2 py-1 rounded-full ml-2 text-gray-600">
                Vendor
              </span>
            </div>
          </div>

          <div>
            <label className="text-[#232323] text-[14px] mb-1 block">
              Select Payment Date
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex gap-3 items-center">
              <button
                onClick={() =>
                  setPaymentDate(new Date().toISOString().split("T")[0])
                }
                className={`w-[120px] ${
                  isToday
                    ? "border border-[#7F599D] text-[#3B0069] bg-[#ECE6F0]"
                    : "bg-gray-200 text-gray-700"
                } h-[38px] rounded-[10px] px-5 py-1 text-sm font-medium`}
              >
                Now
              </button>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="border rounded-lg px-4 py-2 text-[16px] text-[#232323] w-full bg-gray-100 h-[38px]"
              />
            </div>
          </div>

          <div>
            <p className="text-[#232323] text-[16px] mb-2">Payment</p>
            <hr className="mb-3" />
            <div className="flex items-center gap-3 mb-3">
              <label className="text-[#232323] text-[14px] whitespace-nowrap">
                Paid Amount
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative w-full">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" />
                <input
                  type="number"
                  placeholder="Amount"
                  className="pl-8 pr-4 py-2 border rounded-lg text-[15px] w-full bg-gray-100 h-[38px]"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {["Cash", "Online", "Cheque"].map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`px-5 py-1.5 rounded-full text-[15px] font-medium ${
                    paymentMethod === method
                      ? "bg-[#ECE6F0] text-[#3B0069] border border-[#3B0069]"
                      : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {method}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-gray-200">
            <div>
              <label className="text-[#232323] text-[14px] mb-2 block">
                Remarks/ Comments (Optional)
              </label>
              <textarea
                placeholder="Type"
                className="w-full border rounded-lg px-4 py-2 text-[15px] min-h-[72px] bg-gray-100"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            <div>
              <label className="text-[#232323] text-[14px] mb-2 block">
                Payment Slip (Optional)
              </label>
              <input
                type="file"
                id="receiptUpload"
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="hidden"
              />

              {!receiptFile ? (
                <label
                  htmlFor="receiptUpload"
                  className="w-full h-[56px] flex items-center justify-center px-4 py-4 bg-[#ECE6F0] rounded-lg text-[15px] text-[#3B0069] cursor-pointer hover:bg-purple-100"
                >
                  <span className="flex items-center gap-2">
                    <Upload size={16} /> Upload Photos/Documents
                  </span>
                </label>
              ) : (
                <div className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 rounded-lg border border-gray-300">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-sm text-gray-700 truncate">
                      {receiptFile.name}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      ({(receiptFile.size / 1024).toFixed(2)} KB)
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="ml-2 text-red-500 hover:text-red-700"
                    type="button"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}

              <p className="text-sm text-gray-500 mt-1">
                Supported formats: Images (JPG, PNG, GIF), PDF, DOC, DOCX (Max
                5MB)
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-0 mb-0 pb-2 flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          className={`bg-[#3B0069] text-white px-10 py-2 rounded-[12px] font-medium hover:bg-purple-800 flex items-center gap-2 transition-opacity duration-200 ${
            !isFormValid || isSubmitting
              ? "opacity-50 cursor-not-allowed"
              : "opacity-100"
          }`}
        >
          {isSubmitting ? "Saving..." : "Save"}
          <div className="w-6 h-6 rounded-full bg-[#5E2D91] flex items-center justify-center">
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Check className="w-4 h-4 text-white" />
            )}
          </div>
        </button>
      </div>
    </div>
  );
};

export default AdvancePayment;
